import {
  getMultipleRecords,
  getPhaseLabel,
  getRoomLabel,
  getSystemLabel,
  getSystemScopeTagLabel,
  GlobalOptionSetDefinition,
  priceListItemSchema,
  QUERY_KEYS,
  QuoteData,
  sortRoomsHierarchically,
  useQuoteDetailsQuery,
  useQuoteQuery,
  type AccountRoomData,
  type CreatePaymentMilestoneData,
  type PriceListItemData,
  type QuoteDetailData,
} from '@/lib/api'
import { MissingRequiredDataError } from '@/lib/api/missing-required-data-error'
import { dynamicsEnvironment, env, isDev } from '@/lib/env'
import { SessionExpiredError } from '@/lib/session-expired-error'
import { FieldServiceProductType, QuoteType } from '@/lib/types'
import { hashKey, useQuery, type QueryKey } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { clsx, type ClassValue } from 'clsx'
import Decimal from 'decimal.js'
import { getMarginPercentage } from '@/lib/margin'
import { useMemo } from 'react'
import { twMerge } from 'tailwind-merge'
import { z, ZodError } from 'zod'
import { queryClient } from './query-client'
import { calculateMilestoneAmounts } from './payment-milestone-allocation'

export { getMarginPercentage }

export { calculateMilestoneAmounts } from './payment-milestone-allocation'

// ─── Constants: Product IDs ──────────────────────────────────────────────────

export const LABOR_SUMMARY_PRODUCT_NAMES = [
  'LABOR SUMMARY:SERVICE',
  'LABOR SUMMARY:INSTALLATION',
  'LABOR SUMMARY:PROGRAMMING',
  'LABOR SUMMARY:ROUGH-IN',
  'LABOR SUMMARY:DESIGN & ENGINEERING',
  'LABOR SUMMARY:PROJECT MANAGEMENT',
] as const

export const LABOR_SUMMARY_NAME_TO_KEY_MAP = {
  'LABOR SUMMARY:SERVICE': 'dsy_servicelabor',
  'LABOR SUMMARY:INSTALLATION': 'dsy_installationlabor',
  'LABOR SUMMARY:PROGRAMMING': 'dsy_programminglabor',
  'LABOR SUMMARY:ROUGH-IN': 'dsy_roughinlabor',
  'LABOR SUMMARY:DESIGN & ENGINEERING': 'dsy_engineeringlabor',
  'LABOR SUMMARY:PROJECT MANAGEMENT': 'dsy_projectmanagementlabor',
} as const satisfies Record<(typeof LABOR_SUMMARY_PRODUCT_NAMES)[number], string>

export const LABOR_SUMMARY_PRODUCT_ID_MAP = {
  'LABOR SUMMARY:SERVICE': isDev ? '919ad095-34ae-f011-bbd3-7c1e525c4408' : 'fd2601e4-2cae-f011-bbd3-7c1e520a24c8',
  'LABOR SUMMARY:INSTALLATION': 'b58de7b2-d0b6-ef11-b8e8-6045bddb0403',
  'LABOR SUMMARY:PROGRAMMING': '4718a4db-d0b6-ef11-b8e8-6045bddb0403',
  'LABOR SUMMARY:ROUGH-IN': 'b0884b19-d1b6-ef11-b8e8-6045bddb0403',
  'LABOR SUMMARY:DESIGN & ENGINEERING': '731dfc80-d0b6-ef11-b8e8-6045bddb0403',
  'LABOR SUMMARY:PROJECT MANAGEMENT': '2ad9def6-d0b6-ef11-b8e8-6045bddb0403',
} as const satisfies Record<(typeof LABOR_SUMMARY_PRODUCT_NAMES)[number], string>

export const SUMMARY_PRODUCT_ID_MAP = {
  SUPPLIES: '4e3a23cf-d1b6-ef11-b8e8-6045bddb0403',
  ...LABOR_SUMMARY_PRODUCT_ID_MAP,
  'DISCOUNT:EQUIPMENT': '9d04256e-21d9-ef11-a730-000d3a581343',
  'DISCOUNT:LABOR': 'd7193215-afdd-ef11-a730-000d3a99c177',
  'DISCOUNT:EQUIPMENT NOT TAXABLE': 'f1038395-8249-f111-bec6-7c1e5201e13b',
  'DISCOUNT:LABOR NOT TAXABLE': '148b697d-8249-f111-bec6-7c1e5201e13b',
  'DISCOUNT:CUSTOMER LOYALTY': '613a7f63-ab19-f011-998a-6045bdeba466', // Deprecated
  'DISCOUNT:PRODUCTS': 'a1e3142a-addf-ef11-a730-000d3a54d2c8', // Deprecated
} as const

export const PRODUCT_ID_MAP = {
  'SCOPE OF WORK': '040db2d9-49d7-ef11-a730-7c1e5218b4f6',
  // Reserved ad-hoc package header product ($0, null manufacturer, dsy_ispackage).
  PACKAGE: isDev ? 'f61d2f34-e4a1-f111-b8dc-7ced8d19a06c' : '4956f53c-d7ab-f111-aaac-7c1e525b4059',
  ...SUMMARY_PRODUCT_ID_MAP,
} as const

export const PRICE_LIST_ID_MAP = {
  MASTER: '6e388c8b-bcb6-ef11-b8e9-0022482ea922',
}

// ─── Constants: Defaults & Labels ────────────────────────────────────────────

export const REQUIREMENT_MAP: Record<number, { label: string; style: string }> = {
  3: { label: 'Required', style: 'bg-destructive/10 text-destructive' },
  2: { label: 'Recommended', style: 'bg-caution/10 text-caution' },
  1: { label: 'Optional', style: 'bg-complete/10 text-complete' },
}

/** dsy_requirement option values — the keys of REQUIREMENT_MAP, named. */
export const PRODUCT_REQUIREMENT = {
  Optional: 1,
  Recommended: 2,
  Required: 3,
} as const

export const DEFAULT_SUPPLIES_PRICE_RATE = 7 // % of the entire quote
export const DEFAULT_SUPPLIES_COST_RATE = 2.8 // % of the entire quote
export const DEFAULT_WIRE_RUN_LENGTH = 125 // ft
export const DEFAULT_WIRE_LOOP_LENGTH = 25 // ft

export const DEFAULT_IMAGE_URL = './daisy_logomark_sunrise.png' as const

export const QUOTE_TYPE_LABEL: Record<number, string> = {
  [QuoteType.NewSale]: 'Proposal',
  [QuoteType.NewSaleDaisyCare]: 'Proposal',
  [QuoteType.ChangeOrder]: 'Change Order',
  [QuoteType.ServiceInvoice]: 'Service Invoice',
  [QuoteType.Budget]: 'Budget',
}

export function getDocumentLabel(quoteType: number | undefined) {
  return QUOTE_TYPE_LABEL[quoteType ?? -1] ?? 'Proposal'
}

// ─── Tailwind & Window helpers ───────────────────────────────────────────────

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

export function isValidEmail(value: string) {
  return EMAIL_REGEX.test(value.trim())
}

export const isInIframe = window.parent !== window

export function openInNewTab(url: string | URL | null | undefined) {
  if (!url) return

  if (url instanceof URL) {
    url = url.toString()
  }

  if (!z.string().url().safeParse(url).success) {
    return
  }

  const a = document.createElement('a')
  a.href = url
  a.target = '_blank'
  a.rel = 'noopener noreferrer'
  a.click()
}

export function getQuoteId() {
  const quoteId = new URLSearchParams(window.location.search).get('id')
  if (!quoteId) {
    throw new Error('Quote ID is required.')
  }
  // Dataverse returns lookups lowercase; the Dynamics ribbon passes ids braced and uppercase.
  return quoteId.replace(/[{}]/g, '').toLowerCase()
}

const GUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/

/** True when the URL's quote id is a GUID, safe to put straight into a query filter. */
export function hasValidQuoteId() {
  try {
    return GUID_PATTERN.test(getQuoteId())
  } catch {
    return false
  }
}

// ─── Product classification ──────────────────────────────────────────────────

export function isSummaryDetail(productid: string) {
  return Object.values(SUMMARY_PRODUCT_ID_MAP).includes(
    productid as (typeof SUMMARY_PRODUCT_ID_MAP)[keyof typeof SUMMARY_PRODUCT_ID_MAP]
  )
}

export function isLaborSummaryDetail(productid: string) {
  return Object.values(LABOR_SUMMARY_PRODUCT_ID_MAP).includes(
    productid as (typeof LABOR_SUMMARY_PRODUCT_ID_MAP)[keyof typeof LABOR_SUMMARY_PRODUCT_ID_MAP]
  )
}

export function isScopeOfWork(productid: string) {
  return productid === PRODUCT_ID_MAP['SCOPE OF WORK']
}

// Minimal shape the classifiers below need. Quote details expose it as
// detail.productid, price list items as item.dsy_product — both parse through
// productSchema, which carries dsy_ispackage.
type ClassifiableProduct = { productid: string; dsy_ispackage?: boolean | null }

/**
 * Package header row. The ONLY rule is the product's dsy_ispackage flag — never
 * the name or the GUID, because headers bind to an ad-hoc reserved product whose
 * GUID differs per org and whose name is author-supplied.
 *
 * dsy_ispackage reads null (never false) on every row created before the column
 * existed, so this is a strict-true check: a fetch whose $select forgot the flag
 * degrades to "not a header" instead of misclassifying every line.
 */
export function isPackageHeader(product: ClassifiableProduct) {
  return product.dsy_ispackage === true
}

/**
 * A row the quote builder lays out inside a room: real product lines AND package
 * headers. Excludes only the reserved scope-of-work and summary rows.
 *
 * Use this where a header must stay visible or draggable (builder tables,
 * reorder groups); use isProduct for anything that counts, prices, rolls up or
 * exports real products.
 */
export function isLineItem(product: ClassifiableProduct) {
  return !isScopeOfWork(product.productid) && !isSummaryDetail(product.productid)
}

export function isProduct(product: ClassifiableProduct) {
  if (isPackageHeader(product)) return false
  // The reserved header product is a utility product like SCOPE OF WORK: keep it
  // out of the search catalog even if its own dsy_ispackage flag is ever cleared.
  // In prod the map holds a placeholder string, so this never matches until the
  // prod product exists.
  if (product.productid === PRODUCT_ID_MAP.PACKAGE) return false
  return isLineItem(product)
}

/**
 * A CATALOG package: a saved package that owns its own product record, as
 * opposed to the reserved ad-hoc header product every hand-built package binds
 * to. Both read as package headers on a quote; only this one is a catalog entry
 * the product search may offer and the manage dialog may edit.
 */
export function isCatalogPackage(product: ClassifiableProduct) {
  return isPackageHeader(product) && product.productid !== PRODUCT_ID_MAP.PACKAGE
}

/**
 * A row whose room, system and scope tag are its parent's rather than its own.
 *
 * An ACCESSORY inherits them from the product it hangs on; a package MEMBER
 * inherits them from its header, and the two are the same rule. Neither may be
 * moved on its own: a member dragged into another system by its own picker keeps
 * its package key and lands outside the package it still belongs to, which is
 * exactly the divergence this suppresses.
 *
 * So both tables render no bucket picker on such a row and the item form hides
 * Room / System / Scope tag for it. The whole group moves from the parent —
 * a product row's own picker for an accessory, the header's for a member.
 *
 * Install location is NOT one of these: it is where the physical unit goes, not
 * which section the row belongs to, and accessories have always had their own.
 */
export function hasInheritedBucket(quoteDetail: {
  dsy_cpqfields: { dsy_parentglobalid?: string | null; dsy_packageglobalid?: string | null }
}) {
  const fields = quoteDetail.dsy_cpqfields
  return !!fields.dsy_parentglobalid || !!fields.dsy_packageglobalid
}

/**
 * Whether a product has an image to render at all. Quote rows carry the inline
 * base64 thumbnail; other reads carry only the url — either one means there is
 * something to show instead of an icon.
 */
export function hasProductImage(product: { entityimage?: string | null; entityimage_url?: string | null }) {
  return !!product.entityimage || !!product.entityimage_url
}

// Same GUID in dev (daisycodev) and prod (daisyco) orgs, like PRODUCT_ID_MAP.
export const FOOT_UOM_ID = '66f16f39-bab6-ef11-b8e8-7c1e52466abf' as const

type WireProduct = ClassifiableProduct & { name: string; _defaultuomid_value: string | null }

// Only per-foot cables get the default wire run/loop lengths — CABLE* products
// sold "Each" (pre-terminated cables, patch leads) must keep quantity 1.
export function isWireRun(product: WireProduct) {
  return (
    isProduct(product) &&
    product._defaultuomid_value === FOOT_UOM_ID &&
    (product.name.startsWith('CABLE:') || product.name.startsWith('CABLE-PLENUM:')) &&
    !product.name.includes('LOOP')
  )
}

export function isWireLoop(product: WireProduct) {
  return (
    isProduct(product) &&
    product._defaultuomid_value === FOOT_UOM_ID &&
    (product.name.startsWith('CABLE:') || product.name.startsWith('CABLE-PLENUM:')) &&
    product.name.includes('LOOP')
  )
}

/**
 * Quantity the add-items dialog seeds for one row it is about to insert under
 * `parentQuantity` copies of its parent.
 *
 * The wire defaults exist because an accessory link carries no length of its
 * own. A catalog package's contents DO carry one — the length its author
 * captured — so applying the default there would silently reset a saved 300ft
 * run to the quote's 125ft default.
 */
export function getSeededChildQuantity({
  product,
  quantity,
  parentQuantity,
  isPackageContent,
  wireRunLength,
  wireLoopLength,
}: {
  product: WireProduct
  quantity: number | null
  parentQuantity: number
  isPackageContent: boolean
  wireRunLength: number
  wireLoopLength: number
}) {
  if (!isPackageContent) {
    if (isWireRun(product)) return wireRunLength * parentQuantity
    if (isWireLoop(product)) return wireLoopLength * parentQuantity
  }
  return (quantity ?? 0) * parentQuantity
}

// ─── Formatters: currency, hours, dates ──────────────────────────────────────

export function formatCurrency(value: number | null | undefined) {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(value ?? 0)
}

// Virtualized compact tables: rows render at h-10 (2.5rem) plus the 1px cell
// border; room headers at h-9 (2.25rem). Estimates must match the rendered
// heights exactly or the accumulated error shortens the virtual scroll range
// while scrolling, cutting off the bottom rows until measurement catches up.
// Derived from the root font size so browser text-size settings don't
// reintroduce the drift.
export function getVirtualRowEstimates() {
  const rootFontPx = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16
  return { row: 2.5 * rootFontPx + 1, header: 2.25 * rootFontPx }
}

// Block-level tags whose end is a line break once the markup is gone. Void tags
// are absent on purpose — they carry no text to separate.
const HTML_BLOCK_CLOSE_TAG =
  /<\/(?:address|article|aside|blockquote|dd|div|dl|dt|fieldset|figcaption|figure|footer|form|h[1-6]|header|li|main|nav|ol|p|pre|section|table|tr|ul)>/gi

/**
 * Newline markers for <br> and every block boundary, inserted BEFORE the tags
 * are stripped — textContent concatenates across elements with nothing between
 * them, so paragraph breaks have to survive as characters or not at all.
 */
export function markHtmlBlockBreaks(html: string): string {
  return html.replace(/<br\s*\/?>/gi, '\n').replace(HTML_BLOCK_CLOSE_TAG, '\n$&')
}

/**
 * Collapse runs of whitespace WITHIN each line while keeping the line breaks
 * between them.
 *
 * Every run of breaks becomes exactly one: an empty <p> and the newline a
 * pretty-printed document puts between two <p>s are indistinguishable by the
 * time the markup is gone, and inventing blank lines from source formatting is
 * the worse of the two mistakes.
 */
export function collapsePlainTextLines(text: string): string {
  return text
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .join('\n')
    .replace(/\n{2,}/g, '\n')
    .trim()
}

/**
 * Rich text as plain text, block structure intact. Package headers store a rich
 * description but the builder rows and the proposal render it as a plain
 * clamped paragraph, exactly like a product's description — a read-only editor
 * there would draw a second bordered box inside the row.
 *
 * Paragraphs and list items come back as newlines rather than being flattened
 * into one line: the proposal renders this with whitespace-pre-wrap, so a
 * multi-paragraph description reads the way its author wrote it.
 */
export function toPlainText(html: string | null | undefined) {
  if (!html) return ''
  // Parsed into an inert document, never assigned to a live element: a detached
  // div still runs <img onerror> and other load-time handlers on assignment.
  const parsed = new DOMParser().parseFromString(markHtmlBlockBreaks(html), 'text/html')
  return collapsePlainTextLines(parsed.body.textContent ?? '')
}

export function formatHours(hours: number | null | undefined) {
  return new Decimal(hours ?? 0).toDecimalPlaces(2).toString() + ' hr'
}

export function formatDate(date?: string | number | Date | null) {
  if (!date) {
    date = new Date()
  }
  return new Date(date).toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  })
}

export function formatDateTime(date?: string | number | Date | null) {
  if (!date) {
    date = new Date()
  }
  return new Date(date).toLocaleString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

/**
 * Formats a budget range string based on base amount and variance percentages
 * @param baseAmount - The base amount to apply variance to
 * @param lowVariancePercent - The low variance percentage (default 0)
 * @param highVariancePercent - The high variance percentage (default 0)
 * @param fixedDiscountAmount - The flat-dollar discount already applied to the base amount (default 0).
 *   Percentage discounts scale with the variance, so they are excluded here.
 * @returns Formatted string like "$10,000 - $15,000" or just "$12,500" if no variance
 */
export function formatBudgetRange({
  baseAmount,
  lowVariancePercent = 0,
  highVariancePercent = 0,
  fixedDiscountAmount = 0,
}: {
  baseAmount: number | Decimal
  lowVariancePercent?: number | null
  highVariancePercent?: number | null
  fixedDiscountAmount?: number | null
}): string {
  lowVariancePercent = Number(lowVariancePercent)
  highVariancePercent = Number(highVariancePercent)

  const discount = new Decimal(Number(fixedDiscountAmount))
  const amount = new Decimal(baseAmount).plus(discount)

  const lowVarianceFactor = new Decimal(lowVariancePercent).div(100).plus(1)
  const highVarianceFactor = new Decimal(highVariancePercent).div(100).plus(1)

  // Apply variance to the base amount, then subtract the fixed discount
  const lowAmount = amount.mul(lowVarianceFactor).minus(discount).toNumber()
  const highAmount = amount.mul(highVarianceFactor).minus(discount).toNumber()

  if (lowAmount === highAmount) {
    return formatCurrency(lowAmount)
  }

  return `${formatCurrency(lowAmount)} - ${formatCurrency(highAmount)}`
}

// ─── Margin & profitability ──────────────────────────────────────────────────

export function getMarginString(
  amount: number | Decimal | null | undefined,
  unitcost: number | Decimal | null | undefined
) {
  const margin = getMarginPercentage(amount, unitcost)
  return margin.toString() + '%'
}

export function getMarginColor(margin: number) {
  if (margin <= 0) return 'text-destructive'
  if (margin < 45) return 'text-caution'
  return 'text-complete'
}

export function getGPPHColor(gpph: number) {
  if (gpph <= 0) return 'text-destructive'
  if (gpph < 250) return 'text-caution'
  return 'text-complete'
}

// Delta coloring — positive is good by default; pass invert for money-out metrics (e.g. cost).
export function getDeltaColor(value: number, { invert = false }: { invert?: boolean } = {}) {
  if (value === 0) return 'text-muted-foreground'
  const positive = invert ? value < 0 : value > 0
  return positive ? 'text-complete' : 'text-destructive'
}

// ─── Images ──────────────────────────────────────────────────────────────────

/**
 * Base64 prefixes of each format's magic bytes. Dataverse hands back whatever
 * was uploaded, and a package's catalog image is usually a PNG.
 */
const BASE64_IMAGE_MIME: [prefix: string, mime: string][] = [
  ['iVBOR', 'image/png'],
  ['/9j/', 'image/jpeg'],
  ['R0lGOD', 'image/gif'],
  ['UklGR', 'image/webp'],
]

/**
 * Data URL for a raw base64 payload, labelled with the type it actually is.
 *
 * The MIME used to be hardcoded to jpeg, so every PNG entityimage was served as
 * `data:image/jpeg` — browsers sniff and render it anyway, but anything trusting
 * the declared type (a PDF export, a NetSuite hand-off) is handed a lie.
 * Unrecognised bytes keep the old jpeg label rather than refusing to render.
 */
export function getImageUrlFromBase64(rawBase64: string | null | undefined) {
  if (!rawBase64) {
    return DEFAULT_IMAGE_URL
  }
  if (rawBase64.startsWith('data:')) {
    return rawBase64
  }
  const mime = BASE64_IMAGE_MIME.find(([prefix]) => rawBase64.startsWith(prefix))?.[1] ?? 'image/jpeg'
  return `data:${mime};base64,${rawBase64}`
}

// Unauthenticated (proposal mode) thumbnail source — ProductImage handles the
// authenticated sources (inline base64 / Dataverse fetch) itself.
export function getProductImageUrl(product: { productid: string }): string {
  return `${env.VITE_PROPOSAL_API_BASE_URL}/api/product/${product.productid}/image?env=${dynamicsEnvironment}`
}

// ─── React Query helpers ─────────────────────────────────────────────────────

export function getUniqueQueryKeys(queryKeys: QueryKey[]) {
  const seen = new Set<string>()
  const uniqueQueryKeys = []

  for (const key of queryKeys) {
    const hash = hashKey(key)
    if (!seen.has(hash)) {
      uniqueQueryKeys.push(key)
      seen.add(hash)
    }
  }

  return uniqueQueryKeys
}

// ─── Errors ──────────────────────────────────────────────────────────────────

export function deriveErrorMessage(error: unknown): string {
  const fallback = 'An unexpected error occurred. Please try again later.'
  if (!error) return fallback

  // Enriched AxiosError from response interceptor (preferred path)
  if (error instanceof AxiosError && error.userMessage) {
    return error.userMessage
  }

  // Missing required Dataverse data — constructor already builds the capped,
  // deduped display message (the generic Error branch below rejects multiline)
  if (error instanceof MissingRequiredDataError) {
    return error.message
  }

  if (error instanceof SessionExpiredError) {
    return error.message
  }

  // ZodError — surface the schema's custom messages directly
  if (error instanceof ZodError) {
    const messages = [...new Set(error.errors.map((e) => e.message))]
    return messages.slice(0, 3).join('\n')
  }

  // Unenriched AxiosError (safety net — should not happen after interceptor)
  if (error instanceof AxiosError) {
    return fallback
  }

  // Plain Error thrown from our own code — browsers throw TypeError/ReferenceError/etc
  // (subclasses) for runtime issues, so filtering on name === 'Error' lets our authored
  // `throw new Error('...')` messages through while suppressing engine errors that may
  // reference internals. Stack frames live on .stack, not .message, so no need to pattern-match.
  if (error instanceof Error) {
    if (error.name === 'Error' && error.message && error.message.length < 300 && !error.message.includes('\n')) {
      return error.message
    }
    return fallback
  }

  // Raw string
  if (typeof error === 'string' && error.length < 200) return error

  return fallback
}

// ─── Proposal URL ────────────────────────────────────────────────────────────

/** Public proposal URL for a quote, carrying only the environment flags
 * (sandbox/dev) from the current page. */
export function getProposalUrl(quoteId: string | null) {
  const url = new URL(window.location.href)
  const searchParams = new URLSearchParams()

  const sandbox = url.searchParams.get('sandbox')
  const dev = url.searchParams.get('dev')

  if (quoteId) searchParams.set('id', quoteId)
  if (sandbox) searchParams.set('sandbox', sandbox)
  if (dev) searchParams.set('dev', dev)

  return `${url.origin}/proposal${searchParams.toString() ? '?' + searchParams.toString() : ''}`
}

// ─── Quote name parsing ──────────────────────────────────────────────────────

/** Regex to match "Change Order {N}" at end of quote name, optionally followed by " - {description}" */
const CO_NAME_REGEX = /Change Order (\d+)(?:\s*-\s*(.*))?$/
export function parseChangeOrderName(quoteName: string) {
  const match = quoteName.match(CO_NAME_REGEX)
  if (!match) return null
  return {
    number: match[1],
    description: match[2]?.trim() || '',
    prefix: quoteName.slice(0, match.index!),
  }
}

// ─── Quote details: sorting, shared values, search tags ──────────────────────

/**
 * Canonical quote-detail sort. Mirrors proposal-api/utils/sort_quote_details.py.
 *
 * Tier order:
 *   1. Bucket: regular before summary
 *   2. Summary tier: SUMMARY_PRODUCT_ID_MAP insertion order
 *   3. Regular tier:
 *      3a. room DFS pre-order rank (see sortRoomsHierarchically)
 *      3b. Room scope-of-work hoist (all SCOPE OF WORK rows sort first within
 *          room, regardless of system; tied by dsy_grouporder)
 *      3c. system option order
 *      3d. scope-tag option order
 *      3e. dsy_grouporder asc
 *      3f. quotedetailid (deterministic tiebreak — pre-backfill data has
 *          dsy_grouporder=null on every row, so the bucket ties on tier 3e
 *          and JS sort stability would diverge from BE on independent fetches)
 *   4. Package/parent regroup:
 *      4a. A package header sorts at its own dsy_grouporder in its own bucket.
 *      4b. Its members (dsy_packageglobalid = header dsy_globalid) splice
 *          immediately after it, ordered by (dsy_grouporder, quotedetailid) —
 *          the package key wins over the member's own room/system/scope, so
 *          divergent rows never scatter.
 *      4c. Each member's accessories splice after that member.
 *      4d. Accessories (dsy_parentglobalid) splice after their parent, exactly
 *          as before packages existed.
 *   Dangling keys never drop a row: a dsy_packageglobalid whose header is absent
 *   makes the row behave as unpackaged (sorts in its own bucket); a dangling
 *   dsy_parentglobalid keeps the pre-existing behaviour (sorted order, tail).
 *   A row carrying BOTH keys is illegal (server-side XOR plugin) — if one is
 *   ever seen, dsy_parentglobalid wins so the row keeps pre-package behaviour.
 */
export function sortQuoteDetails(
  quoteDetails: QuoteDetailData[],
  rooms: AccountRoomData[],
  systems: GlobalOptionSetDefinition,
  systemScopeTags: GlobalOptionSetDefinition,
): QuoteDetailData[] {
  // Rank rooms 1..N in DFS pre-order: top-level rooms by (sequencenumber,
  // room_id) — so unsequenced rooms don't collide at 0 and interleave their
  // items across rooms — with each parent immediately followed by its children
  // in the same order. Flat data ranks exactly as it did before nesting.
  const sortedRooms = sortRoomsHierarchically(rooms)
  const roomIdToIndexMap = new Map(sortedRooms.map((r, i) => [r.dsy_accountroomid, i + 1]))
  const systemIdToIndexMap = new Map(systems.Options.map((s, i) => [s.Value, i + 1]))
  const systemScopeTagToIndexMap = new Map(systemScopeTags.Options.map((s, i) => [s.Value, i + 1]))
  const summaryProductIds = Object.values(SUMMARY_PRODUCT_ID_MAP)
  const summaryProductIdToIndexMap = new Map<string, number>(
    summaryProductIds.map((productId, i) => [productId, i + 1])
  )
  const scopeOfWorkProductId = PRODUCT_ID_MAP['SCOPE OF WORK']

  type SortKey = [number, number, number, number, number, number, number, string]
  const sortKey = (d: QuoteDetailData): SortKey => {
    const pid = d.productid?.productid ?? ''
    const tieId = d.quotedetailid ?? ''
    const summaryTier = summaryProductIdToIndexMap.get(pid)
    if (summaryTier !== undefined) {
      return [1, summaryTier, 0, 0, 0, 0, 0, tieId]
    }
    const cpq = d.dsy_cpqfields
    const roomOrder = roomIdToIndexMap.get(cpq._dsy_room_value ?? '') ?? 0
    const groupOrder = cpq.dsy_grouporder ?? 0
    if (pid === scopeOfWorkProductId) {
      return [0, 0, roomOrder, 0, 0, 0, groupOrder, tieId]
    }
    const systemOrder = cpq.dsy_system != null ? systemIdToIndexMap.get(cpq.dsy_system) ?? 0 : 0
    const scopeTagOrder = cpq.dsy_scopetag != null ? systemScopeTagToIndexMap.get(cpq.dsy_scopetag) ?? 0 : 0
    return [0, 0, roomOrder, 1, systemOrder, scopeTagOrder, groupOrder, tieId]
  }

  const sorted = [...quoteDetails].sort((a, b) => {
    const ka = sortKey(a)
    const kb = sortKey(b)
    for (let i = 0; i < 7; i++) {
      const av = ka[i] as number
      const bv = kb[i] as number
      if (av !== bv) return av - bv
    }
    const aId = ka[7] as string
    const bId = kb[7] as string
    if (aId < bId) return -1
    if (aId > bId) return 1
    return 0
  })

  // Splice package members after their header and accessories after their parent.
  const presentGlobalIds = new Set(sorted.map((d) => d.dsy_cpqfields.dsy_globalid).filter(Boolean))
  const childrenByParent = new Map<string, QuoteDetailData[]>()
  const membersByPackage = new Map<string, QuoteDetailData[]>()
  const topLevel: QuoteDetailData[] = []
  for (const detail of sorted) {
    const parentId = detail.dsy_cpqfields.dsy_parentglobalid
    const packageId = detail.dsy_cpqfields.dsy_packageglobalid
    if (parentId) {
      const arr = childrenByParent.get(parentId)
      if (arr) arr.push(detail)
      else childrenByParent.set(parentId, [detail])
    } else if (packageId && presentGlobalIds.has(packageId)) {
      const arr = membersByPackage.get(packageId)
      if (arr) arr.push(detail)
      else membersByPackage.set(packageId, [detail])
    } else {
      // No package key, or a dangling one: sorts in its own bucket.
      topLevel.push(detail)
    }
  }
  // Members carry the header's bucket, so order them on their own grouporder
  // rather than the flat key, which would scatter divergent rows.
  for (const members of membersByPackage.values()) {
    members.sort((a, b) => {
      const ga = a.dsy_cpqfields.dsy_grouporder ?? 0
      const gb = b.dsy_cpqfields.dsy_grouporder ?? 0
      if (ga !== gb) return ga - gb
      const ia = a.quotedetailid ?? ''
      const ib = b.quotedetailid ?? ''
      return ia < ib ? -1 : ia > ib ? 1 : 0
    })
  }

  const result: QuoteDetailData[] = []
  // Deleting each map entry as it is consumed makes a self- or mutually
  // referencing package key terminate (the rows fall to the tail) instead of
  // recursing forever.
  const emit = (detail: QuoteDetailData) => {
    result.push(detail)
    const globalId = detail.dsy_cpqfields.dsy_globalid
    if (!globalId) return
    const members = membersByPackage.get(globalId)
    if (members) {
      membersByPackage.delete(globalId)
      for (const member of members) emit(member)
    }
    const children = childrenByParent.get(globalId)
    if (children) {
      childrenByParent.delete(globalId)
      result.push(...children)
    }
  }
  for (const parent of topLevel) emit(parent)
  // Orphans (parent not in set) retain sorted order at the end.
  for (const orphans of childrenByParent.values()) result.push(...orphans)
  // Members whose header exists but was itself never emitted (an accessory
  // header, a key cycle) follow — never dropped.
  for (const members of membersByPackage.values()) result.push(...members)
  return result
}

/**
 * Rows hidden because their package is collapsed — the transitive member set:
 * every member of a collapsed header, plus those members' accessories. Returns
 * quotedetailids so callers can filter a row list before it reaches a table.
 *
 * Collapse is asked about per header, so the builder (a Zustand map) and the
 * proposal (local state seeded from the quote's default) share one filter.
 */
export function getCollapsedPackageMemberIds(
  quoteDetails: QuoteDetailData[] | undefined,
  isPackageCollapsed: (headerGlobalId: string) => boolean
): Set<string> {
  const hidden = new Set<string>()
  if (!quoteDetails?.length) return hidden

  const collapsedHeaderIds = new Set(
    quoteDetails
      .filter((detail) => isPackageHeader(detail.productid))
      .map((detail) => detail.dsy_cpqfields.dsy_globalid)
      .filter((globalId): globalId is string => !!globalId && isPackageCollapsed(globalId))
  )
  if (!collapsedHeaderIds.size) return hidden

  const hiddenMemberGlobalIds = new Set<string>()
  for (const detail of quoteDetails) {
    const { dsy_globalid, dsy_parentglobalid, dsy_packageglobalid } = detail.dsy_cpqfields
    // The accessory key wins on an illegal both-keys row, matching the sort.
    if (dsy_parentglobalid) continue
    if (!dsy_packageglobalid || !collapsedHeaderIds.has(dsy_packageglobalid)) continue
    hidden.add(detail.quotedetailid)
    if (dsy_globalid) hiddenMemberGlobalIds.add(dsy_globalid)
  }
  if (!hiddenMemberGlobalIds.size) return hidden

  for (const detail of quoteDetails) {
    const parentGlobalId = detail.dsy_cpqfields.dsy_parentglobalid
    if (parentGlobalId && hiddenMemberGlobalIds.has(parentGlobalId)) hidden.add(detail.quotedetailid)
  }
  return hidden
}

export function getOrphanedProductQuoteDetails(quoteDetails: QuoteDetailData[] | undefined): QuoteDetailData[] {
  if (!quoteDetails) return []

  const globalIds = new Set(quoteDetails.map((detail) => detail.dsy_cpqfields.dsy_globalid).filter(Boolean))
  return quoteDetails.filter((detail) => {
    const parentGlobalId = detail.dsy_cpqfields.dsy_parentglobalid
    return isLineItem(detail.productid) && !!parentGlobalId && !globalIds.has(parentGlobalId)
  })
}

export function getVisibleParentProductQuoteDetails({
  quoteDetails,
  allQuoteDetails = quoteDetails,
  includeFilteredAccessories = false,
}: {
  quoteDetails: QuoteDetailData[] | undefined
  allQuoteDetails?: QuoteDetailData[] | undefined
  includeFilteredAccessories?: boolean
}): QuoteDetailData[] {
  if (!quoteDetails) return []

  const allGlobalIds = new Set(allQuoteDetails?.map((detail) => detail.dsy_cpqfields.dsy_globalid).filter(Boolean))
  const topLevelGlobalIds = new Set(
    quoteDetails
      .filter((detail) => {
        const parentGlobalId = detail.dsy_cpqfields.dsy_parentglobalid
        return isLineItem(detail.productid) && (!parentGlobalId || !allGlobalIds.has(parentGlobalId))
      })
      .map((detail) => detail.dsy_cpqfields.dsy_globalid)
      .filter(Boolean)
  )

  return quoteDetails.filter((detail) => {
    // isLineItem, not isProduct: this feeds the builder tables, where a package
    // header must render as a row so its members can nest and reorder under it.
    if (!isLineItem(detail.productid)) return false

    const parentGlobalId = detail.dsy_cpqfields.dsy_parentglobalid
    if (!parentGlobalId) return true
    if (!allGlobalIds.has(parentGlobalId)) return true

    return includeFilteredAccessories && !topLevelGlobalIds.has(parentGlobalId)
  })
}

export function getProductSearchtags(product: PriceListItemData, excludeManufacturer = false) {
  const tags = [
    getSystemLabel(product.dsy_systemsearchtag),
    getSystemScopeTagLabel(product.dsy_scopesearchtag),
    product.dsy_searchtag1text,
    product.dsy_searchtag2text,
    product.dsy_searchtag3text,
    product.dsy_searchtag4text,
    product.dsy_searchtag5text,
    product.dsy_searchtag6text,
  ]
  const hasRealTags = tags.some(Boolean)
  if (!excludeManufacturer && hasRealTags) {
    tags.push(product.dsy_manufacturertext)
  }
  return tags.filter(Boolean).map((tag) => tag?.toUpperCase().replace(/\s/g, '-')) as string[]
}

export function getSharedValue<T>(
  quoteDetails: QuoteDetailData[],
  getter: (detail: QuoteDetailData) => T | null | undefined
): T | undefined {
  if (quoteDetails.length === 0) return undefined

  // Helper to normalize values for comparison (treat null and undefined as the same)
  const normalizeValue = (value: T | null | undefined): string => {
    if (value === null || value === undefined) return ''
    // Use JSON.stringify for complex types, otherwise convert to string
    if (typeof value === 'object') {
      return JSON.stringify(value)
    }
    return String(value)
  }

  // Count occurrences of each value and track first occurrence index
  const valueCounts = new Map<string, { value: T | null | undefined; count: number; firstIndex: number }>()

  for (let i = 0; i < quoteDetails.length; i++) {
    const detail = quoteDetails[i]
    const value = getter(detail)
    const normalized = normalizeValue(value)

    if (valueCounts.has(normalized)) {
      valueCounts.get(normalized)!.count++
    } else {
      valueCounts.set(normalized, { value, count: 1, firstIndex: i })
    }
  }

  // Find the most common value, preferring the one that appears first in case of ties
  let mostCommon: { value: T | null | undefined; count: number; firstIndex: number } | null = null
  for (const entry of valueCounts.values()) {
    if (
      !mostCommon ||
      entry.count > mostCommon.count ||
      (entry.count === mostCommon.count && entry.firstIndex < mostCommon.firstIndex)
    ) {
      mostCommon = entry
    }
  }

  // Return the most common value if it's not null/undefined, otherwise return the first value
  if (mostCommon && mostCommon.value !== null && mostCommon.value !== undefined) {
    return mostCommon.value as T
  }

  // Fallback to first non-null/undefined value, or first value if all are null/undefined
  const firstValue = getter(quoteDetails[0])
  return firstValue !== null && firstValue !== undefined ? (firstValue as T) : undefined
}

// ─── Payment milestones ──────────────────────────────────────────────────────

export type PaymentMilestoneTemplate = {
  name: string
  milestones: Omit<CreatePaymentMilestoneData, 'dsy_amount'>[]
  default?:
    | QuoteType.NewSale
    | QuoteType.ChangeOrder
    | QuoteType.ServiceInvoice
    | QuoteType.Budget
    | QuoteType.NewSaleDaisyCare
}

/** "Change Order N - description", or "Change Order N" without a description. */
export function getDefaultChangeOrderMilestoneName(quoteName: string) {
  const changeOrderName = parseChangeOrderName(quoteName)
  if (!changeOrderName) return 'Change Order'
  const label = `Change Order ${changeOrderName.number}`
  return changeOrderName.description ? `${label} - ${changeOrderName.description}` : label
}

export function getPaymentMilestoneTemplates(): PaymentMilestoneTemplate[] {
  const quoteId = getQuoteId()
  const quote = queryClient.getQueryData<QuoteData>(QUERY_KEYS.quote())
  if (!quote) {
    throw new Error('Quote not found')
  }
  return [
    {
      name: 'Installation',
      milestones: [
        {
          dsy_name: 'Prior to Ordering Materials',
          dsy_percent: 65,
          dsy_sequencenumber: 1,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
        {
          dsy_name: 'Prior to Installation',
          dsy_percent: 25,
          dsy_sequencenumber: 2,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
        {
          dsy_name: 'Substantial Completion',
          dsy_percent: 10,
          dsy_sequencenumber: 3,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
      ],
      default: QuoteType.NewSale,
    },
    {
      name: 'Change Order',
      milestones: [
        {
          dsy_name: getDefaultChangeOrderMilestoneName(quote.name),
          dsy_percent: 100,
          dsy_sequencenumber: 1,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
      ],
      default: QuoteType.ChangeOrder,
    },
    {
      name: 'Service',
      milestones: [
        {
          dsy_name: 'Service',
          dsy_percent: 100,
          dsy_sequencenumber: 1,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
      ],
      default: QuoteType.ServiceInvoice,
    },
    {
      name: 'Budget',
      milestones: [
        {
          dsy_name: 'Retainer (Non-Refundable)',
          dsy_percent: 2,
          dsy_sequencenumber: 1,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
      ],
      default: QuoteType.Budget,
    },
    {
      name: 'Construction Project',
      milestones: [
        {
          dsy_name: 'Deposit',
          dsy_percent: 5,
          dsy_sequencenumber: 1,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
        {
          dsy_name: 'Prior to Ordering Rough-In Materials',
          dsy_percent: 10,
          dsy_sequencenumber: 2,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
        {
          dsy_name: 'Rough-In Completion',
          dsy_percent: 10,
          dsy_sequencenumber: 3,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
        {
          dsy_name: 'Prior to Ordering Installation Materials',
          dsy_percent: 50,
          dsy_sequencenumber: 4,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
        {
          dsy_name: 'Prior to Installation',
          dsy_percent: 15,
          dsy_sequencenumber: 5,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
        {
          dsy_name: 'Substantial Completion',
          dsy_percent: 10,
          dsy_sequencenumber: 6,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
      ],
    },
    {
      name: 'Lighting Design',
      milestones: [
        {
          dsy_name: 'Prior to Design',
          dsy_percent: 40,
          dsy_sequencenumber: 1,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
        {
          dsy_name: 'Design Completion',
          dsy_percent: 30,
          dsy_sequencenumber: 2,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
        {
          dsy_name: 'Rough-In Completion',
          dsy_percent: 20,
          dsy_sequencenumber: 3,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
        {
          dsy_name: 'Substantial Completion',
          dsy_percent: 10,
          dsy_sequencenumber: 4,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
      ],
    },
    {
      name: 'Small Project (90/10)',
      milestones: [
        {
          dsy_name: 'Deposit',
          dsy_percent: 90,
          dsy_sequencenumber: 1,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
        {
          dsy_name: 'Substantial Completion',
          dsy_percent: 10,
          dsy_sequencenumber: 2,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
      ],
      default: QuoteType.NewSaleDaisyCare,
    },
    {
      name: '50/50',
      milestones: [
        {
          dsy_name: 'Deposit',
          dsy_percent: 50,
          dsy_sequencenumber: 1,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
        {
          dsy_name: 'Substantial Completion',
          dsy_percent: 50,
          dsy_sequencenumber: 2,
          'dsy_quote@odata.bind': `/quotes(${quoteId})`,
        },
      ],
    },
  ]
}

export function getDefaultMilestones(summaryTotal: number): CreatePaymentMilestoneData[] {
  const quote = queryClient.getQueryData<QuoteData>(QUERY_KEYS.quote())
  if (!quote) {
    throw new Error('Quote not found')
  }
  const templates = getPaymentMilestoneTemplates()
  const template = templates.find((t) => t.default === quote.dsy_type) || templates[0]
  return calculateMilestoneAmounts(template.milestones, summaryTotal)
}

/**
 * Calculates payment milestone amounts with proper rounding to ensure they sum to the exact total.
 * Also removes duplicates based on name and percent.
 * @param milestones - Array of milestones with percentages
 * @param total - Total amount to distribute across milestones
 * @returns Updated milestones with calculated amounts that sum exactly to the total
 */
// ─── Changes formatting ──────────────────────────────────────────────────────

export const CHANGE_FIELD_LABELS: Record<string, string> = {
  description: 'Description',
  quantity: 'Quantity',
  priceperunit: 'Unit Price',
  msdyn_costpriceperunit: 'Unit Cost',
  msdyn_taxable: 'Taxable',
  room: 'Room',
  system: 'System',
  phase: 'Phase',
  location: 'Location',
  laborHours: 'Unit labor hours',
  engineering: 'Engineering hours',
  installation: 'Installation hours',
  roughin: 'Rough-in hours',
  programming: 'Programming hours',
  projectManagement: 'Project management hours',
  service: 'Service hours',
}

export function formatChangeValue(
  key: string,
  value: string | number | boolean | null,
  changeType: 'new' | 'changed' | 'deleted'
): string {
  if ((key === 'priceperunit' || key === 'msdyn_costpriceperunit') && (typeof value === 'number' || value === null)) {
    return changeType === 'deleted' ? `-${formatCurrency(value || 0)}` : formatCurrency(value || 0)
  }
  if (key === 'msdyn_taxable' && (typeof value === 'boolean' || value === null)) {
    return value ? 'Yes' : 'No'
  }
  if (key === 'room' && (typeof value === 'string' || value === null)) {
    return getRoomLabel(value) || 'Unassigned'
  }
  if (key === 'system' && (typeof value === 'number' || value === null)) {
    return getSystemLabel(value) || 'Unassigned'
  }
  if (key === 'phase' && (typeof value === 'number' || value === null)) {
    return getPhaseLabel(value) || 'Unassigned'
  }
  return String(value)
}

// ─── CSV export ──────────────────────────────────────────────────────────────

const CSV_HEADERS = [
  '#',
  'Global ID',
  'Parent Global ID',
  'Package Global ID',
  'Name',
  'Description',
  'Quantity',
  'Unit Cost',
  'Unit Sell Price',
  'Margin %',
  'Taxable',
  'Phase',
  'Room',
  'System',
  'Scope Tag',
  'Location',
  'Engineering Labor (hrs)',
  'Installation Labor (hrs)',
  'Rough-In Labor (hrs)',
  'Programming Labor (hrs)',
  'Project Management Labor (hrs)',
  'Labor Factor %',
  'Product Name',
  'Part Number',
  'Product Type',
]

function escapeCSV(value: string | number | boolean): string {
  const s = String(value)
  if (s.includes(',') || s.includes('\n') || s.includes('"')) {
    return `"${s.replace(/"/g, '""')}"`
  }
  return s
}

function getProductTypeLabel(type: number | null | undefined): string {
  if (type === FieldServiceProductType.Labor) return 'Labor'
  if (type === FieldServiceProductType.Inventory) return 'Inventory'
  return 'Non-Inventory'
}

export function downloadQuoteDetailsCSV(
  quoteDetails: (QuoteDetailData & { __displayIndex?: number })[],
  options?: { quoteNumber?: string; quoteName?: string },
) {
  // Caller passes details in canonical sort order; honor stamped __displayIndex
  // (e.g. grid view's filtered subset keeps the canonical full-quote line number),
  // otherwise emit row index+1.
  const rows = quoteDetails.map((detail, index) => [
    detail.__displayIndex ?? index + 1,
    detail.dsy_cpqfields.dsy_globalid,
    detail.dsy_cpqfields.dsy_parentglobalid ?? '',
    detail.dsy_cpqfields.dsy_packageglobalid ?? '',
    (detail.productid.dsy_ispackage ? detail.quotedetailname : detail.productid.name),
    detail.description ?? '',
    detail.quantity,
    detail.msdyn_costpriceperunit ?? 0,
    detail.priceperunit ?? 0,
    getMarginPercentage(detail.priceperunit, detail.msdyn_costpriceperunit).toNumber() ?? 0,
    detail.msdyn_taxable ?? '',
    getPhaseLabel(detail.dsy_cpqfields.dsy_phase) ?? '',
    getRoomLabel(detail.dsy_cpqfields._dsy_room_value) ?? '',
    getSystemLabel(detail.dsy_cpqfields.dsy_system) ?? '',
    getSystemScopeTagLabel(detail.dsy_cpqfields.dsy_scopetag) ?? '',
    detail.dsy_cpqfields.dsy_location ?? '',
    detail.dsy_cpqfields.dsy_engineeringlabor ?? 0,
    detail.dsy_cpqfields.dsy_installationlabor ?? 0,
    detail.dsy_cpqfields.dsy_roughinlabor ?? 0,
    detail.dsy_cpqfields.dsy_programminglabor ?? 0,
    detail.dsy_cpqfields.dsy_projectmanagementlabor ?? 0,
    detail.dsy_cpqfields.dsy_laborfactorpercent ?? 0,
    detail.productid.name,
    detail.productid.dsy_partnumber ?? '',
    getProductTypeLabel(detail.productid.msdyn_fieldserviceproducttype),
  ])

  const csv = [CSV_HEADERS.map(escapeCSV).join(','), ...rows.map((r) => r.map(escapeCSV).join(','))].join('\n')

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
  const url = URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.setAttribute('href', url)
  const slug = [options?.quoteNumber, options?.quoteName]
    .filter(Boolean)
    .join('-')
    .replace(/[^a-zA-Z0-9-_ ]/g, '')
    .trim()
    .replace(/\s+/g, '-')
  const date = new Date().toISOString().split('T')[0]
  const filename = slug ? `${slug}-line-items-${date}.csv` : `quote-line-items-${date}.csv`
  link.setAttribute('download', filename)
  link.style.visibility = 'hidden'
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  URL.revokeObjectURL(url)
}

// ─── TV mount compatibility ──────────────────────────────────────────────────

export const VIDEO_SYSTEM_ID = 780100000
export const VIDEO_DISPLAY_SCOPE_TAG = 309890000

export type CompatStatus = 'fit' | 'bad' | 'unknown'

export type MountCompat = {
  status: CompatStatus
  reason: string | null
}

export type MountSpecs = {
  minSize: number | null
  maxSize: number | null
  maxWeight: number | null
}

export type ReferenceTVResult = {
  tv: PriceListItemData | null
  tvs: PriceListItemData[]
}

const VESA_PATTERN = /(\d{2,4})\s*[x×]\s*(\d{2,4})/gi
const SUBCATEGORY_RANGE = /\[(?:<=\s*)?(\d{1,3})(?:\s*-\s*(\d{1,3}))?"?\]/

const UNIT = `(?:"|''|inches?|in\\.?)`
const RANGE = new RegExp(`(\\d{2,3})\\s*${UNIT}?\\s*-\\s*(\\d{2,3})\\s*${UNIT}`, 'i')
const UP_TO = new RegExp(`up to\\s*(\\d{2,3})\\s*${UNIT}`, 'i')
const WEIGHT_ANCHORED = new RegExp(
  `(?:max(?:imum)?|up\\s*to|holds?|supports?|capacity|load(?:\\s*rating)?|rating)[^\\d]{0,20}(\\d{2,4}(?:\\.\\d+)?)\\s*(?:lb|lbs|pound|pounds)\\b\\.?` +
    `|(\\d{2,4}(?:\\.\\d+)?)\\s*(?:lb|lbs|pound|pounds)\\b\\.?\\s*(?:load|capacity|max|maximum|rating)`,
  'gi'
)

function normalizeMountText(raw: string): string {
  return raw
    .replace(/[“”″]/g, '"')
    .replace(/[‘’′]/g, "'")
    .replace(/[–—−]/g, '-')
    .replace(/\s+/g, ' ')
}

export function parseTVVesaPattern(raw: string | null | undefined): string | null {
  if (!raw) return null
  const m = new RegExp(VESA_PATTERN.source, 'i').exec(raw)
  if (!m) return null
  return `${parseInt(m[1], 10)}x${parseInt(m[2], 10)}`
}

export function parseMountVesaPatterns(raw: string | null | undefined): Set<string> {
  const out = new Set<string>()
  if (!raw) return out
  for (const m of raw.matchAll(VESA_PATTERN)) {
    out.add(`${parseInt(m[1], 10)}x${parseInt(m[2], 10)}`)
  }
  return out
}

export function parseMountSpecs(description: string | null | undefined): MountSpecs {
  if (!description) return { minSize: null, maxSize: null, maxWeight: null }
  const text = normalizeMountText(description)

  let minSize: number | null = null
  let maxSize: number | null = null
  const rangeMatch = text.match(RANGE)
  if (rangeMatch) {
    const a = parseInt(rangeMatch[1], 10)
    const b = parseInt(rangeMatch[2], 10)
    if (a <= b && a >= 10 && b <= 200) {
      minSize = a
      maxSize = b
    }
  } else {
    const upToMatch = text.match(UP_TO)
    if (upToMatch) {
      const n = parseInt(upToMatch[1], 10)
      if (n >= 10 && n <= 200) maxSize = n
    }
  }

  let maxWeight: number | null = null
  for (const m of text.matchAll(WEIGHT_ANCHORED)) {
    const raw = m[1] ?? m[2]
    if (!raw) continue
    const n = parseFloat(raw)
    if (n > 0 && n <= 2000 && (maxWeight === null || n > maxWeight)) {
      maxWeight = n
    }
  }

  return { minSize, maxSize, maxWeight }
}

export function getTVScreenSize(tv: PriceListItemData): number | null {
  const tagSize = tv.dsy_searchtag2text ? parseInt(tv.dsy_searchtag2text, 10) : NaN
  if (Number.isFinite(tagSize) && tagSize >= 10 && tagSize <= 200) {
    return tagSize
  }
  const sub = tv.dsy_product.dsy_subcategory
  if (sub) {
    const m = sub.match(SUBCATEGORY_RANGE)
    if (m) {
      const lo = parseInt(m[1], 10)
      const hi = m[2] ? parseInt(m[2], 10) : lo
      if (Number.isFinite(lo) && Number.isFinite(hi)) return Math.round((lo + hi) / 2)
    }
  }
  return null
}

export function getTVWeight(tv: PriceListItemData): number | null {
  const w = tv.dsy_product.dsy_weight
  return typeof w === 'number' && w > 0 ? w : null
}

export function computeMountCompat(tv: PriceListItemData, mount: PriceListItemData): MountCompat {
  const specs = parseMountSpecs(mount.dsy_description)
  const size = getTVScreenSize(tv)
  const weight = getTVWeight(tv)
  const tvVesa = parseTVVesaPattern(tv.dsy_product.dsy_vesapattern)
  const mountVesa = parseMountVesaPatterns(mount.dsy_product.dsy_vesapattern)

  const reasons: string[] = []
  let hasSignal = false
  let failed = false

  if (size != null && (specs.minSize != null || specs.maxSize != null)) {
    hasSignal = true
    if (specs.maxSize != null && size > specs.maxSize) {
      failed = true
      reasons.push(`TV ${size}" > max ${specs.maxSize}"`)
    } else if (specs.minSize != null && size < specs.minSize) {
      failed = true
      reasons.push(`TV ${size}" < min ${specs.minSize}"`)
    }
  }

  if (weight != null && specs.maxWeight != null) {
    hasSignal = true
    if (weight > specs.maxWeight) {
      failed = true
      reasons.push(`TV ${weight} lbs > max ${specs.maxWeight} lbs`)
    }
  }

  if (tvVesa && mountVesa.size > 0) {
    hasSignal = true
    if (!mountVesa.has(tvVesa)) {
      failed = true
      reasons.push(`VESA ${tvVesa} ≠ ${[...mountVesa].join(', ')}`)
    }
  }

  if (!hasSignal) return { status: 'unknown', reason: null }
  if (failed) return { status: 'bad', reason: reasons.join(' · ') }

  const fitParts: string[] = []
  if (size != null && (specs.minSize != null || specs.maxSize != null)) {
    if (specs.minSize != null && specs.maxSize != null) fitParts.push(`${specs.minSize}-${specs.maxSize}"`)
    else if (specs.maxSize != null) fitParts.push(`≤${specs.maxSize}"`)
    else if (specs.minSize != null) fitParts.push(`≥${specs.minSize}"`)
  }
  if (weight != null && specs.maxWeight != null) fitParts.push(`≤${specs.maxWeight} lbs`)
  if (tvVesa && mountVesa.size > 0) fitParts.push(`VESA ${tvVesa}`)
  return { status: 'fit', reason: fitParts.join(' · ') || null }
}

const PLI_SELECT =
  '_dsy_product_value,_dsy_pricelist_value,dsy_unitcost,dsy_unitprice,dsy_taxable,dsy_description,dsy_engineeringlabor,dsy_installationlabor,dsy_roughinlabor,dsy_programminglabor,dsy_projectmanagementlabor,dsy_servicelabor,dsy_systemsearchtag,dsy_scopesearchtag,dsy_searchtag1text,dsy_searchtag2text,dsy_searchtag3text,dsy_searchtag4text,dsy_searchtag5text,dsy_searchtag6text,dsy_manufacturertext'

const PLI_EXPAND =
  'dsy_product($select=productid,name,msdyn_fieldserviceproducttype,dsy_defaultphase,entityimage_url,_defaultuomid_value,dsy_partnumber,statuscode,dsy_weight,dsy_subcategory,dsy_vesapattern,dsy_ispackage)'

function encodeODataLiteral(value: string): string {
  return encodeURIComponent(value.replace(/'/g, "''"))
}

export function useReferenceTVForRoom(roomId: string | undefined | null): ReferenceTVResult {
  const { data: quote } = useQuoteQuery()
  const priceListId = quote?._pricelevelid_value ?? null
  const isRealRoom = !!roomId && !roomId.startsWith('temp-')
  const { data: roomDetails } = useQuoteDetailsQuery(
    isRealRoom ? { roomId: roomId!, systemId: VIDEO_SYSTEM_ID } : undefined
  )

  const displayProductIds = useMemo(() => {
    if (!isRealRoom || !roomDetails) return []
    const ids = new Set<string>()
    for (const detail of roomDetails) {
      if (detail.dsy_cpqfields.dsy_scopetag === VIDEO_DISPLAY_SCOPE_TAG) {
        ids.add(detail.productid.productid)
      }
    }
    return Array.from(ids)
  }, [isRealRoom, roomDetails])

  const enabled = !!priceListId && displayProductIds.length > 0
  const { data } = useQuery({
    queryKey: QUERY_KEYS.referenceTV({
      priceListId: priceListId ?? '',
      productIds: displayProductIds,
    }),
    enabled,
    staleTime: 30_000,
    queryFn: async ({ signal }): Promise<PriceListItemData[]> => {
      if (!priceListId || displayProductIds.length === 0) return []
      const productFilter = `Microsoft.Dynamics.CRM.In(PropertyName='dsy_product',PropertyValues=[${displayProductIds
        .map((id) => `'${encodeODataLiteral(id)}'`)
        .join(',')}])`
      const filter = `_dsy_pricelist_value eq '${encodeODataLiteral(priceListId)}' and ${productFilter}`
      const rows = await getMultipleRecords<PriceListItemData>(
        'dsy_pricelistitems',
        `?$select=${PLI_SELECT}&$expand=${PLI_EXPAND}&$filter=${filter}`,
        { signal }
      )
      const parsed = rows.map((r) => priceListItemSchema.safeParse(r))
      return parsed
        .filter((r): r is z.SafeParseSuccess<PriceListItemData> => r.success)
        .map((r) => r.data)
    },
  })

  return useMemo<ReferenceTVResult>(() => {
    if (!data || data.length === 0) return { tv: null, tvs: [] }
    const tvs = data.filter((pli) => pli.dsy_searchtag1text?.toUpperCase() === 'TV')
    if (tvs.length === 0) return { tv: null, tvs: [] }
    const sorted = [...tvs].sort((a, b) => {
      const wa = a.dsy_product.dsy_weight ?? 0
      const wb = b.dsy_product.dsy_weight ?? 0
      if (wa !== wb) return wb - wa
      const sa = getTVScreenSize(a) ?? 0
      const sb = getTVScreenSize(b) ?? 0
      return sb - sa
    })
    return { tv: sorted[0], tvs: sorted }
  }, [data])
}
